import { uploadAttachment } from '../api/upload-attachment';

import type {
  AttachmentAdapter,
  PendingAttachment,
  CompleteAttachment,
  Attachment,
} from '@assistant-ui/react';

const ALLOWED_TYPES = 'image/png,image/jpeg,image/webp,application/pdf,text/plain,text/markdown';

/**
 * Creates an AttachmentAdapter that wraps the existing S3 presigned upload flow
 * into assistant-ui's attachment lifecycle.
 *
 * - add(): Queues file with requires-action status (upload deferred until send)
 * - send(): Uploads via uploadAttachment() and returns S3 key in content
 * - remove(): No-op — assistant-ui manages attachment list internally
 */
export function createAttachmentAdapter(params: {
  orgId: string;
  projectId: string;
  sessionId: string;
}): AttachmentAdapter {
  return {
    accept: ALLOWED_TYPES,

    add({ file }: { file: File }): Promise<PendingAttachment> {
      const type: PendingAttachment['type'] = file.type.startsWith('image/')
        ? 'image'
        : file.type === 'application/pdf'
          ? 'document'
          : 'file';

      const id =
        // eslint-disable-next-line n/no-unsupported-features/node-builtins
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? // eslint-disable-next-line n/no-unsupported-features/node-builtins
            crypto.randomUUID()
          : String(Date.now());

      return Promise.resolve({
        id,
        type,
        name: file.name,
        contentType: file.type,
        file,
        status: { type: 'requires-action', reason: 'composer-send' },
      });
    },

    async send(attachment: PendingAttachment): Promise<CompleteAttachment> {
      const s3Key = await uploadAttachment(
        params.orgId,
        params.projectId,
        params.sessionId,
        attachment.file,
      );

      return {
        id: attachment.id,
        type: attachment.type,
        name: attachment.name,
        contentType: attachment.contentType,
        content: [{ type: 'text', text: s3Key }],
        status: { type: 'complete' },
      };
    },

    async remove(_attachment: Attachment): Promise<void> {
      // No-op: assistant-ui manages the attachment list internally.
      // The attachment is removed by the runtime after this resolves.
    },
  };
}
